import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from "../context/AuthContext";
import { doc, collection, query, orderBy, onSnapshot, updateDoc, serverTimestamp } from "firebase/firestore";
import { FaTimes, FaBars, FaSignOutAlt, FaPaperPlane, FaPlus, FaTimesCircle, FaChevronRight } from "react-icons/fa";
import './Workspaces.css';
import Profile from "../components/workspaces/Profile";
import React from "react";
import { db, auth } from "../firebase";
import { signOut } from "firebase/auth";
import BuyCredits from "./BuyCredits";
import { createWebsite } from '../firebaseHelpers';
import { createWebsiteInfo } from '../kairosFunctions';
import { getGeminiTokenUsage } from '../ai-usageFunctions';
import { createProjectFileStructure } from '../websiteSturctureFunctions';

const Workspaces = () => {
  const { currentUser } = useAuth(); // use standard 'user' from AuthContext
  const navigate = useNavigate();

  const userId = currentUser?.uid;

  const [websites, setWebsites] = useState([]);
  const [userData, setUserData] = useState(null);
  const [websiteDesc, setWebsiteDesc] = useState();
  const [buyCredits, setBuyCredits] = useState(false);
  const [more, setMore] = useState(false); // ✅ mobile drawer toggle
  const [profileOpen, setProfileOpen] = useState(false);
  const dropdownRef = useRef();
  const textareaRef = useRef(null);
  const [userSettings, setUserSettings] = useState(null);
  const [working, setWorking] = useState();
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [fileStructure, setFileStructure] = useState(null);
  
  // example values – replace with your real logic values
  // const [estimatedTokens, setEstimatedTokens] = useState(0);
  const estimatedCredits = 25; // optional if you have it

  const handleInput = () => {
    const textarea = textareaRef.current;
    textarea.style.height = "auto";
    textarea.style.height = textarea.scrollHeight + "px";
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      console.log("User signed out");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const updateSettings = async (patch) => {
    if (!userId) return;
  
    // 1️⃣ Update local state immediately
    setUserSettings(prev => ({
      ...prev,
      ...patch,
    }));
  
    try {
      // 2️⃣ Persist to Firestore
      const settingsRef = doc(db, "users", userId, "settings", "main");
  
      await updateDoc(settingsRef, {
        ...patch,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Failed to update settings:", error);
    }
  };

  const handleSend = async () => {
  
    if (userData?.credits > 25) {
      setWorking(true);
      try {
        const meta = await createWebsiteInfo({ userRequest: websiteDesc, chattingName: userData?.name });
        const result = meta?.result;
        // If meta is a string, parse it
        const safeMeta = typeof meta === "string" ? JSON.parse(meta) : meta;
        const websiteName = safeMeta.websiteName;
        const websiteDescription = safeMeta.websiteDescription;
        const friendlyResponse = safeMeta.friendlyResponse?.trim() || `Hey — I have set up a website called "${meta.websiteName}". I’ll set it up now and we can edit it together.`;
        const userAction = safeMeta.userAction;
        const kairosAction = safeMeta.kairosAction;
        const messageType = "Setup";

        console.log(result);
    
        // Store in state (example)
        const createdWebsite = await createWebsite({
          userId,
          websiteName,
          websiteDescription,
          friendlyResponse,
          websiteDesc,
          messageType,
          userAction,
          kairosAction
        });
        
        if (createdWebsite.success) {
          const websiteId = createdWebsite.newWebsiteId;
          await getGeminiTokenUsage({ result, model: "gemini-2.5-flash-lite", userId, websiteId, userMessage: websiteDesc, action: "Initial Website Website Name Generation." });
          const structureResults = await createProjectFileStructure({ websiteId, userId });
          setFileStructure(structureResults);
          console.log("Website File Structure:\n", structureResults, fileStructure);
          setWorking(false);
          setWebsiteDesc("");
          console.log("Website created: ", websiteId);
          navigate(`/kairos/${websiteId}`)
        }
      } catch (error) {
        console.error("[handleSend] ❌ AI ERROR (raw):", error);
      } finally {
        console.log("[handleSend] done");
      }
    } else {
      setShowCreditsModal(true);
      setWorking(false);
    }
  };

  // Only redirect after we know auth state has loaded
  useEffect(() => {
    if (currentUser === undefined) return; // wait for auth to initialize

    if (currentUser === null) {
      navigate("/");
      return;
    }
  }, [currentUser, navigate]);

  // Fetch user data
  useEffect(() => {
    if (!userId) return;
  
    const userDocRef = doc(db, "users", userId);
  
    const unsubscribe = onSnapshot(
      userDocRef,
      (userSnap) => {
        if (userSnap.exists()) {
          setUserData(userSnap.data());
        } else {
          console.warn("User document does not exist.");
          setUserData(null);
        }
      },
      (error) => {
        console.error("Error listening to user data:", error);
      }
    );
  
    // cleanup listener when userId changes or component unmounts
    return () => unsubscribe();
  }, [userId]);

  // Listen to settings doc (real-time)
    useEffect(() => {
      if (!userId) return;
  
      const settingsRef = doc(db, "users", userId, "settings", "main");
      const unsub = onSnapshot(
        settingsRef,
        (snap) => {
          if (!snap.exists()) {
            setUserSettings(null);
            return;
          }
          const data = snap.data();
          setUserSettings(data);
        },
        (err) => console.error("Settings listener error:", err)
      );
  
      return () => unsub();
    }, [userId]);

  // Fetch websites
  useEffect(() => {
    if (!userId) return;
  
    const sitesCol = collection(db, "workspaces", userId, "websites");
    const q = query(sitesCol, orderBy("createdAt", "desc"));
  
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setWebsites(snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })));
      },
      (error) => {
        console.error("Error listening to websites:", error);
      }
    );
  
    // cleanup listener
    return () => unsubscribe();
  }, [userId]);

  // ✅ If user opens drawer on mobile, then changes to desktop, close the mobile drawer state
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setMore(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <main className="workspaces">
      <header className="top-bar">
        {/* ✅ More button: only visible on MOBILE (controlled by CSS) */}
        <div
          className="more"
          ref={dropdownRef}
          onClick={() => setMore(prev => !prev)}
        >
          <FaBars className="more-icon" />
        </div>

        <div className="top-right">
          <button
            className="buy-credits-button"
            onClick={() => {
              navigate("/buy-credits", {replace: true})
            }}
          >
            <FaPlus className="add-icon" />
          </button>

          <button className="icon-btn">
            <p>Buy</p>
            <FaPaperPlane className="add-icon" />
          </button>

          <img
            className="avatar"
            src={userData?.picture || "https://i.pravatar.cc/40"}
            alt="user"
            onClick={() => {
              setProfileOpen(true);
              setMore(false);
            }}
          />
        </div>
      </header>

      <section className="home-section">
        <div className="intro-text-box">
          {userSettings?.marketingPrompt && !working && 
            <div className="marketing-prompt">
                Receive updates?
                <button onClick={() =>
                  updateSettings({
                    receiveMarketing: true,
                    marketingPrompt: false,
                  })
                }>
                  Yes
                </button>
              
                <button onClick={() =>
                  updateSettings({
                    receiveMarketing: false,
                    marketingPrompt: false,
                  })
                }>
                  No
                </button>

                <FaTimes
                  className="close-prompt" 
                  onClick={() =>
                    updateSettings({
                      receiveMarketing: false,
                      marketingPrompt: false,
                    })
                  }
                />
            </div>
          }
          {!working && 
          <>
            <h1>
              Welcome, <span className="gradient-text">{userData?.name}</span>
            </h1>
            <h2>
              Tell <span className="gradient-text">Kairos</span> what you want to build.
            </h2>
          </>
          }
          {working && 
          <>
            <h1>
              <span className="gradient-text">Kairos</span> is working.
            </h1>
            <h2>
              <span className="gradient-text">You'll</span> be redirected shortly.
            </h2>
          </>
          }
        </div>

        <div className="kairos-chat-section">
          <div className="kairos-chat-section-row">
            <div className="chat-add" ref={dropdownRef}>
              <FaPlus className="chat-add-icon" />
            </div>

            <div className="textarea-row">
              <textarea
                className="website-desc"
                type="website-desc"
                rows={1}
                placeholder="Ask Kairos"
                value={websiteDesc}
                onChange={(e) => setWebsiteDesc(e.target.value)}
                onInput={handleInput}
                ref={textareaRef}
                disabled={working}
              />

              <button className="send-icon"
                onClick={handleSend}
                disabled={working}
              >
                <FaPaperPlane />
              </button>
            </div>
          </div>
        </div>
      </section>

      {buyCredits &&
         <BuyCredits 
          onClose={() => setBuyCredits(false)}
         />
      }

      {/* ✅ Overlay should ONLY show on MOBILE (CSS hides it on desktop) */}
      {more && (
        <div className="drawer-overlay" onClick={() => setMore(false)} />
      )}

      {/* ✅ Drawer: On desktop it is always visible via CSS, on mobile it opens via "more" */}
      <div className={`drawer ${more ? "open" : ""}`}>
        <div className="drawer-actions">
          <div className="drawer-header">
            <h3>Actions</h3>

            {/* ✅ Close icon should only be visible on MOBILE (CSS hides it on desktop) */}
            <FaTimesCircle className="close-icon" onClick={() => setMore(false)} />
          </div>

          <nav className="drawer-nav">
            <button>Showcases</button>
            <button>Published</button>
          </nav>

          <div className="drawer-header">
            <h3>My Websites</h3>
          </div>

          <ul className="websites-list">
            {websites.length === 0 ? (
              <div className="no-websites-container">
                <p>No websites yet</p>
              </div>
            ) : (
              websites.map((site) => (
                <li key={site.id}>
                  <button
                    className="website"
                    onClick={() => navigate(`/kairos/${site.id}`)}
                  >
                    {site.name}
                    <FaChevronRight className="arrow-icon" />
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>

        <div className="drawer-profile-section">
          <div className="credits-row">
          <h3><span className="gradient-text-credits">{Number(userData?.credits || 0).toFixed(2)}</span> Credits</h3>
            <button
              className="buy-credits-button"
              onClick={() => {
                navigate("/buy-credits", {replace: true})
              }}
            >
              <FaPlus className="add-icon" />
            </button>
          </div>

          <div className="profile-row">
            <div
              className="name-container"
              onClick={() => {
                setProfileOpen(true);
                setMore(false);
              }}
            >
              <h3>{userData?.name}</h3>
            </div>

            <div className="logout-wrapper-box">
              <FaSignOutAlt onClick={handleSignOut} className="logout-icon" />
            </div>
          </div>
        </div>
      </div>

      {profileOpen &&
        <Profile
          userId={userId}
          userData={userData}
          websites={websites}
          onClose={() => setProfileOpen(false)}
        />
      }

      {showCreditsModal && (
        <div className="kairos-modal-overlay" role="dialog" aria-modal="true">
          <div className="kairos-modal">
            <div className="kairos-modal-header">
              <h3 className="kairos-modal-title">Not enough credits</h3>

              <button
                className="kairos-modal-close"
                onClick={() => setShowCreditsModal(false)}
                aria-label="Close"
                type="button"
              >
                <FaTimesCircle />
              </button>
            </div>

            <div className="kairos-modal-body">
              <p className="kairos-modal-text">
                You don’t have enough credits to complete this action.
              </p>

              <div className="kairos-modal-metrics">
                {/* Optional: only show if you have estimatedCredits */}
                {estimatedCredits !== null && estimatedCredits !== undefined && (
                  <div className="kairos-metric">
                    <span className="kairos-metric-label">Estimated credits</span>
                    <span className="kairos-metric-value">{Number(estimatedCredits || 0).toLocaleString()}</span>
                  </div>
                )}

                {/* Optional: show user's current credits if you want */}
                {userData?.credits !== null && userData?.credits !== undefined && (
                  <div className="kairos-metric">
                    <span className="kairos-metric-label">Your credits</span>
                    <span className="kairos-metric-value">{Number(userData?.credits || 0).toLocaleString()}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="kairos-modal-footer">
              <button
                className="kairos-btn ghost"
                type="button"
                onClick={() => setShowCreditsModal(false)}
              >
                Close
              </button>

              <button
                className="kairos-btn primary"
                type="button"
                onClick={() => navigate("/buy-credits")}  // ✅ change route to your real page
              >
                Buy credits
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default Workspaces;
